// Acoustic Calculator Vue App Configuration
(function() {
  'use strict';

  // Wait for data to be available
  function initializeApp() {
    if (typeof window.AcousticCalculatorData === 'undefined') {
      console.error('AcousticCalculatorData is not loaded.');
      return;
    }

    const data = window.AcousticCalculatorData;

    // Export the Vue app configuration
    window.AcousticCalculatorApp = {
      data() {
        return {
          ...data,
          restoringSettings: false,
          settings: { ...data.defaultSettings },
          uploadedFile: null,
        };
      },

      computed: {
        // === SHARED/COMMON PROPERTIES ===
        wallArea() {
          return (
            2 * this.settings.room_length * this.settings.room_height +
            2 * this.settings.room_width * this.settings.room_height
          );
        },

        floorArea() {
          return this.settings.room_length * this.settings.room_width;
        },

        roomVolume() {
          return this.settings.room_length * this.settings.room_width * this.settings.room_height;
        },

        roomFloorArea() {
          return this.settings.room_length * this.settings.room_width;
        },

        percentages() {
          return [0, 20, 40, 60, 80, 100];
        },

        // === WALLS-SPECIFIC ===
        wallRooms() {
          return this.rooms;
        },

        rt60() {
          const wall = this.wallArea;
          const floor = this.floorArea;

          const effectiveArea =
            0.01 * wall * this.settings.soft_surfaces_wall * 0.3 +
            floor * 0.01 * this.settings.floor_coverage * 0.3 +
            15 * this.settings.plush_seats +
            210 * this.settings.open_entryways;

          const rt60Value = 0.049 * (this.roomVolume / effectiveArea);
          return parseFloat(rt60Value.toFixed(2));
        },

        reverbTime() {
          const wall = this.wallArea;
          const floor = this.floorArea;

          const effectiveArea =
            0.01 * wall * this.settings.soft_surfaces_wall * 0.3 +
            floor * 0.01 * this.settings.floor_coverage * 0.3 +
            15 * this.settings.plush_seats +
            210 * this.settings.open_entryways;

          return Math.round(0.049 * (this.roomVolume / effectiveArea) * 100) / 100;
        },

        deltaRT() {
          const a = Math.log(0.001) / (4 * this.roomVolume);
          const deltaArea = this.targetabsorption - this.effectiveArea;
          const deltaRT = 2 * a * deltaArea * this.roomVolume * this.rt60 ** 2;
          return isNaN(deltaRT) ? 0 : deltaRT;
        },

        effectiveArea() {
          const wall = this.wallArea;
          const floor = this.floorArea;

          return (
            0.01 * wall * this.settings.soft_surfaces_wall * 0.3 +
            floor * 0.01 * this.settings.floor_coverage * 0.3 +
            15 * this.settings.plush_seats +
            210 * this.settings.open_entryways
          );
        },

        targetabsorption() {
          const wall = this.wallArea;
          const floor = this.floorArea;

          const effectiveArea =
            0.01 * wall * this.settings.soft_surfaces_wall * 0.3 +
            floor * 0.01 * this.settings.floor_coverage * 0.3 +
            15 * this.settings.plush_seats +
            210 * this.settings.open_entryways;

          const a = this.roomVolume * 0.049 / effectiveArea;
          const result = this.roomVolume * 0.049 / (a / 2);
          return isNaN(result) ? 0 : result;
        },

        recDeltaSabinsGood() {
          const rt60 = this.rt60;
          const targetRT = Math.max(0.9, rt60 / 2);
          const deltaRT = rt60 - targetRT;
          const recAbsorption = deltaRT > 0 ? this.roomVolume * 0.049 / targetRT : 0;
          return Math.round(Math.max(0, recAbsorption - this.effectiveArea));
        },

        recDeltaSabinsBetter() {
          const rt60 = this.rt60;
          const targetRT = Math.max(0.7, rt60 / 2);
          const deltaRT = rt60 - targetRT;
          const recAbsorption = deltaRT > 0 ? this.roomVolume * 0.049 / targetRT : 0;
          return Math.round(Math.max(0, recAbsorption - this.effectiveArea));
        },

        recDeltaSabinsBest() {
          const rt60 = this.rt60;
          const targetRT = Math.max(0.5, rt60 / 2);
          const deltaRT = rt60 - targetRT;
          const recAbsorption = deltaRT > 0 ? this.roomVolume * 0.049 / targetRT : 0;
          return Math.round(Math.max(0, recAbsorption - this.effectiveArea));
        },

        squareFeetRecGood() {
          return Math.max(this.minimumGoodRec, Math.round(this.recDeltaSabinsGood / 2.5));
        },

        squareFeetRecBetter() {
          return Math.max(this.minimumBestRec, Math.round(this.recDeltaSabinsBetter / 2.5));
        },

        squareFeetRecBest() {
          return Math.max(this.minimumBestRec, Math.round(this.recDeltaSabinsBest / 2.5));
        },

        quantityMultiplier() {
          return 1;
        },

        displayRecGood() {
          return Math.ceil(this.squareFeetRecGood * this.quantityMultiplier);
        },

        displayRecBetter() {
          return Math.ceil(this.squareFeetRecBetter * this.quantityMultiplier);
        },

        displayRecBest() {
          return Math.ceil(this.squareFeetRecBest * this.quantityMultiplier);
        },

        bestStatement() {
          const selectedRooms = this.settings.room_use || [];
          const usagePercentage = Math.round(this.squareFeetRecBest / this.wallArea * 100);

          let statement = selectedRooms.map(room =>
            room.label && `For ${room.label.toLowerCase()} with RT60 = ${this.rt60}s, we recommend ${this.squareFeetRecBest} ft2 of acoustic tiles for a target RT60 of 0.5s.`
          ).join(' ');

          if (statement && usagePercentage > 0) {
            statement += ` This is ${usagePercentage}% of your wall area.`;
          }

          return statement || 'Select room usage to see recommendations';
        },

        // === CEILING-SPECIFIC ===
        ceilingRooms() {
          return this.roomTypes;
        },

        hasCriticalAudioRecording() {
          return this.settings.room_use_ceiling_ids &&
                 this.settings.room_use_ceiling_ids.includes('criticalAudioRecording');
        },

        ceiling_selectedProduct() {
          const products = this.ceilingProducts;
          const selectedValue = this.settings.ceiling_product;
          return products.find(p => p.value === selectedValue) || products[0];
        },

        ceiling_selectedUnit() {
          return this.ceiling_selectedProduct ? this.ceiling_selectedProduct.unit : 'units';
        },

        ceiling_selectedProductUrl() {
          return this.ceiling_selectedProduct ? this.ceiling_selectedProduct.url : '#';
        },

        ceiling_shopButtonText() {
          return this.ceiling_selectedProduct ? this.ceiling_selectedProduct.button : 'Shop';
        },

        ceiling_results() {
          const roomIds = this.settings.room_use_ceiling_ids || [];

          if (roomIds.length === 0) {
            return { good: 0, better: 0, best: 0 };
          }

          // Get absorption coefficients for selected materials
          const wallMaterial = this.wallMaterials.find(m => m.value === this.settings.wall_material_ceiling);
          const ceilingMaterial = this.ceilingMaterials.find(m => m.value === this.settings.ceiling_material);
          const floorMaterial = this.floorMaterials.find(m => m.value === this.settings.floor_material_ceiling);

          if (!wallMaterial || !ceilingMaterial || !floorMaterial) {
            return { good: 0, better: 0, best: 0 };
          }

          // Calculate areas
          const wallArea = 2 * (this.settings.room_length + this.settings.room_width) * this.settings.room_height;
          const ceilingArea = this.settings.room_length * this.settings.room_width;
          const floorArea = ceilingArea;
          const volume = this.roomVolume;

          // Calculate current absorption
          const currentAbsorption =
            wallArea * wallMaterial.absorption +
            ceilingArea * ceilingMaterial.absorption +
            floorArea * floorMaterial.absorption * (1 - this.settings.carpeting_percentage_ceiling / 100) +
            floorArea * 0.3 * (this.settings.carpeting_percentage_ceiling / 100) +
            210 * this.settings.open_entryways_ceiling;

          // Calculate current RT60
          const currentRT60 = 0.049 * volume / currentAbsorption;

          // Get target RT60 based on room types
          let targetRT60 = 1.0; // default

          // Map room types to target RT60 values
          const roomTargets = {
            'conferenceRoom': 0.6,
            'executiveOffice': 0.6,
            'openOffice': 0.9,
            'meetingRoom': 0.7,
            'classroom': 0.7,
            'library': 0.8,
            'restaurant': 1.0,
            'gym': 1.2,
            'criticalAudioRecording': 0.4
          };

          // Use the lowest target RT60 from selected room types
          roomIds.forEach(id => {
            if (roomTargets[id]) {
              targetRT60 = Math.min(targetRT60, roomTargets[id]);
            }
          });

          // Calculate required additional absorption
          const targetAbsorption = 0.049 * volume / targetRT60;
          const requiredAdditionalAbsorption = Math.max(0, targetAbsorption - currentAbsorption);

          // Calculate coverage based on selected product
          const product = this.ceiling_selectedProduct;
          const absorptionPerUnit = product ? product.absorption : 4.0; // default

          const good = Math.ceil(requiredAdditionalAbsorption * 0.6 / absorptionPerUnit);
          const better = Math.ceil(requiredAdditionalAbsorption * 0.8 / absorptionPerUnit);
          const best = Math.ceil(requiredAdditionalAbsorption / absorptionPerUnit);

          // Check if we should show "Contact Sales"
          if (volume > this.ceilingMaxVolume) {
            return { good: 'Contact Sales', better: 'Contact Sales', best: 'Contact Sales' };
          }

          return { good, better, best };
        },

        ceiling_bestMessage() {
          const roomIds = this.settings.room_use_ceiling_ids || [];
          const rooms = roomIds.map(id => {
            const room = this.roomTypes.find(r => r.id === id);
            return room ? room.label : '';
          }).filter(Boolean);

          if (rooms.length === 0) {
            return 'Select room usage to see recommendations';
          }

          return `For ${rooms.join(', ').toLowerCase()}, this provides optimal acoustic treatment.`;
        }
      },

      methods: {
        updateCeilingRoomSelection() {
          this.settings.room_use_ceiling = this.roomTypes.filter(room =>
            this.settings.room_use_ceiling_ids.includes(room.id)
          );
        },


        storeSettings() {
          if (this.restoringSettings) return;
          // Prevent infinite recursion - don't call $root.storeSettings if we're already in storeSettings
          if (this._storingSettings) return;
          this._storingSettings = true;

          try {
            localStorage.setItem('acousticCalculatorSettings', JSON.stringify(this.settings));
          } catch (e) {
            console.warn('Could not store settings:', e);
          } finally {
            this._storingSettings = false;
          }
        },

        restoreSettings() {
          this.restoringSettings = true;
          if (this.$root && this.$root.restoreSettings) {
            this.$root.restoreSettings();
          } else {
            try {
              const stored = localStorage.getItem('acousticCalculatorSettings');
              if (stored) {
                const parsed = JSON.parse(stored);
                Object.assign(this.settings, parsed);
              }
            } catch (e) {
              console.warn('Could not restore settings:', e);
            }
          }
          this.$nextTick(() => {
            this.restoringSettings = false;
          });
        },

        handleFileUpload(event) {
          const file = event.target.files[0];
          if (file) {
            const validTypes = ['image/jpeg', 'image/jpg', 'application/pdf'];
            if (validTypes.includes(file.type)) {
              this.uploadedFile = file;
            } else {
              alert('Please upload a JPG or PDF file.');
              event.target.value = '';
            }
          }
        },

        removeFile() {
          this.uploadedFile = null;
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = '';
          }
        },

        formatFileSize(bytes) {
          if (bytes === 0) return '0 Bytes';
          const k = 1024;
          const sizes = ['Bytes', 'KB', 'MB', 'GB'];
          const i = Math.floor(Math.log(bytes) / Math.log(k));
          return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        },

        formatNumber(num) {
          return Math.round(num).toLocaleString();
        },

        formatNumberWithCommas(num) {
          return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },

        scrollToContactForm(e) {
          if (e) e.preventDefault();
          const form = document.getElementById('contact-form');
          if (form) {
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        },

        async requestInfo() {
          console.log(this.settings, 'sending message');
          const submissionSettings = { ...this.settings, to: 'sales@feltright.com' };

          if (window.AcousticsReportPayload?.createAcousticsReportRequest) {
            try {
              const reportRequest = await window.AcousticsReportPayload.createAcousticsReportRequest(this);
              submissionSettings.acoustics_report_request_id = reportRequest.report_request_id;
            } catch (error) {
              console.warn('Unable to create acoustics report request before contact submission', error);
              submissionSettings.acoustics_report_request_failed = true;
            }
          } else {
            submissionSettings.acoustics_report_request_failed = true;
          }

          // Create FormData to handle file upload
          const formData = new FormData();
          formData.append('data', JSON.stringify(submissionSettings));

          if (submissionSettings.acoustics_report_request_id) {
            formData.append('acoustics_report_request_id', submissionSettings.acoustics_report_request_id);
          }

          // Add the file if one was uploaded
          if (this.uploadedFile) {
            formData.append('file', this.uploadedFile);
          }

          // Send with proper headers for multipart/form-data
          axios.post('https://studiov2.feltright.com/api/submit-message', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then((response) => {
            if (!response.data.error) {
              Swal.fire({
                icon: 'success',
                text: 'Your message has been received, we will respond ASAP',
              });
              // Clear the file after successful submission
              this.uploadedFile = null;
              if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
              }
            }
          });
        },
      },

      mounted() {
        this.restoreSettings();

        // Initialize ceiling room selection if needed
        if (this.settings.room_use_ceiling_ids && this.settings.room_use_ceiling_ids.length > 0) {
          this.updateCeilingRoomSelection();
        }
      }
    };
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp);
  } else {
    initializeApp();
  }
})();